import { environment } from "@/config/env";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useEliteReservation,
  useEliteStructure,
} from "@/contexts/EliteTemporaryContextProvider";
import { Badge } from "@/components/ui/badge";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import * as LucideIcons from "lucide-react";
import { Check, ChevronsUpDown, Plug, Search } from "lucide-react";
import { cn, getTranslatedName } from "@/lib/utils";
import { SERVICE_CATEGORY_TYPE } from "@/types/ServiceCategory";
import { Spinner } from "@/components/ui/spinner";
import PoiCard from "@/components/PoiCard";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function PointOfInterest() {
  const navigate = useNavigate();
  const { structure } = useEliteStructure();
  const { t, i18n } = useTranslation("common");

  useDocumentTitle(
    t("pointOfInterest.title") !== "pointOfInterest.title"
      ? t("pointOfInterest.title")
      : "Punti d'Interesse",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [poi, setPoi] = useState<any[]>([]);

  function getPoiText(val: any): string {
    if (!val) return "";
    let translations = val;
    if (typeof val === "string") {
      try {
        translations = JSON.parse(val);
      } catch {
        return val;
      }
    }
    if (typeof translations === "object" && translations !== null) {
      let language = i18n.language === "en" ? "gb" : i18n.language;
      return (
        translations?.[language] ||
        translations?.["it"] ||
        Object.values(translations)[0] ||
        ""
      );
    }
    return String(val);
  }

  function toPascalCase(value: string) {
    return value
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("");
  }

  function getCategoryIcon(iconName?: string) {
    const iconKey = toPascalCase(iconName || "Plug");
    return (LucideIcons as any)[iconKey] ?? Plug;
  }

  function normalizeBase64Image(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith("data:image/")) return trimmed;
    if (/^[A-Za-z0-9+/=\r\n]+$/.test(trimmed)) {
      return `data:image/png;base64,${trimmed.replace(/\s+/g, "")}`;
    }
    return undefined;
  }

  function getCategoryName(category: any): string {
    if (!category) return "";
    const rawVal =
      category?.name ?? category?.description ?? category?.title ?? category;
    return getTranslatedName(rawVal, i18n.language);
  }

  const filteredItems = (poi || []).filter((item) => {
    const matchesCategory =
      selectedCategories.length === 0 ||
      (item.serviceCategoryId != null &&
        selectedCategories.includes(String(item.serviceCategoryId)));
    const poiName = getPoiText(item.name);
    const matchesSearch = poiName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  function toggleCategory(value: string) {
    setSelectedCategories((prev) => {
      return prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
    });
  }

  function fetchCategories() {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios
      .get(`concierge/servicecategory/${structure.id}`)
      .then((response) => {
        let categoriesArr = (response.data?.serviceCategory || [])
          .filter(
            (category: any) =>
              Number(category?.categoryType) ===
              SERVICE_CATEGORY_TYPE.POINT_OF_INTEREST,
          )
          .map((category: any) => ({
            ...category,
            id: String(category.id),
            customIcon: category.customIcon ?? "",
            defaultIcon: category.defaultIcon ?? "",
          }))
          .sort((a: any, b: any) => {
            const positionA = Number(a?.position ?? a?.sort ?? 0);
            const positionB = Number(b?.position ?? b?.sort ?? 0);
            return positionA - positionB;
          });
        setCategories(categoriesArr);
      })
      .catch(() => {});
  }

  function getPoiList() {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios
      .get(`concierge/pointofinterest/${structure.id}`)
      .then((response) => {
        let poiArr = response.data?.poi || [];
        setPoi(poiArr);
      })
      .catch(() => {
        navigate("/expired", { replace: true });
      });
  }

  useEffect(() => {
    if (structure?.id) {
      getPoiList();
      fetchCategories();
    }
  }, [structure?.id]);

  return !poi ? (
    <div className="flex justify-center p-3">
      <h1 className="text-2xl uppercase font-bold flex gap-2 justify-item-center">
        <Spinner className="size-10" />
      </h1>
    </div>
  ) : (
    <div className="size-full min-h-screen flex justify-center p-3">
      <div className="w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-2"
        >
          <Badge className="mb-4">{t("pointOfInterest.description")}</Badge>
        </motion.div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6 sm:items-center">
          {categories.length > 0 && (
            <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={categoryOpen}
                  className="w-full sm:w-[260px] justify-between h-10 px-3 shrink-0"
                >
                  <div className="flex items-center gap-1.5 overflow-hidden text-left truncate mr-2 flex-1">
                    {selectedCategories.length === 0 ? (
                      <span className="text-muted-foreground truncate">
                        {t("allCategories")}
                      </span>
                    ) : selectedCategories.length === 1 ? (
                      (() => {
                        const category = categories.find(
                          (c: any) => c.id === selectedCategories[0],
                        );
                        const CategoryIcon = getCategoryIcon(
                          category?.defaultIcon,
                        );
                        const customIconSrc = normalizeBase64Image(
                          category?.customIcon,
                        );
                        return (
                          <Badge
                            variant="secondary"
                            className="text-xs shrink-0 max-w-[180px] truncate"
                          >
                            {customIconSrc ? (
                              <img
                                src={customIconSrc}
                                alt="Category icon"
                                className="h-3 w-3 mr-1 shrink-0"
                              />
                            ) : (
                              <CategoryIcon className="h-3 w-3 mr-1 shrink-0" />
                            )}
                            <span className="truncate">
                              {getCategoryName(category) ??
                                selectedCategories[0]}
                            </span>
                          </Badge>
                        );
                      })()
                    ) : (
                      <div className="flex items-center gap-1 overflow-hidden">
                        <Badge
                          variant="secondary"
                          className="text-xs shrink-0 max-w-[130px] truncate"
                        >
                          {(() => {
                            const category = categories.find(
                              (c: any) => c.id === selectedCategories[0],
                            );
                            return (
                              <span className="truncate">
                                {getCategoryName(category) ??
                                  selectedCategories[0]}
                              </span>
                            );
                          })()}
                        </Badge>
                        <Badge variant="secondary" className="text-xs shrink-0">
                          +{selectedCategories.length - 1}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <Command>
                  <CommandInput placeholder={t("searchByName")} />
                  <CommandList>
                    <CommandEmpty>{t("noCategoriesFound")}</CommandEmpty>
                    <CommandGroup>
                      {selectedCategories.length > 0 && (
                        <CommandItem
                          value="all_categories_reset"
                          onSelect={() => setSelectedCategories([])}
                          className="cursor-pointer text-muted-foreground font-medium"
                        >
                          <Check className="mr-2 h-4 w-4 opacity-0" />
                          {t("allCategories")}
                        </CommandItem>
                      )}
                      {categories.map((category) => {
                        const isSelected = selectedCategories.includes(
                          category.id,
                        );
                        const CategoryIcon = getCategoryIcon(
                          category.defaultIcon,
                        );
                        const customIconSrc = normalizeBase64Image(
                          category.customIcon,
                        );

                        return (
                          <CommandItem
                            key={category.id}
                            value={`${category.id}_${getCategoryName(category)}`}
                            onSelect={() => toggleCategory(category.id)}
                            className="cursor-pointer"
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0",
                                isSelected ? "opacity-100" : "opacity-0",
                              )}
                            />
                            <div className="flex items-center gap-2 truncate">
                              {customIconSrc ? (
                                <img
                                  src={customIconSrc}
                                  alt="Category icon"
                                  className="h-3.5 w-3.5 object-cover rounded-sm shrink-0"
                                />
                              ) : (
                                <CategoryIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              )}
                              <span className="truncate">
                                {getCategoryName(category)}
                              </span>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}

          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t("searchByName")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div>
          <p className="text-sm text-gray-600">
            {filteredItems.length}{" "}
            {filteredItems.length === 1 ? t("result") : t("results")}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className={`grid md:grid-cols-3 gap-5 mb-5 mt-5`}
        >
          {filteredItems.map((poiItem: any, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              className="min-w-0"
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
            >
              <PoiCard poi={poiItem} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
